import React from 'react';

import { StyleSheet } from 'react-native';

import { VisuallyHidden } from 'components/ucl/visually-hidden';
import { Box } from 'components/ui';
import { t } from 'state/intl/translations';
import { DeliveryStatus } from 'state/order/types';
import { spacing } from 'styles/constants';
import { STATUS_MESSAGES, STATUS_PERCENTS } from 'utils/get-delivery-heading';

import ProgressBar from '../components/progress-bar/progress-bar';
import ProgressCheckpoint from '../components/progress-bar/progress-checkpoint';
import {
  ProgressList,
  ProgressListItem,
  StatusMessage,
  StatusMessageContainer,
} from '../components/progress-bar/styled';

interface IDeliveryProgressProps {
  deliveryStatus: DeliveryStatus;
}

const DeliveryProgress: React.FC<React.PropsWithChildren<IDeliveryProgressProps>> = ({
  deliveryStatus,
}) => {
  const percentComplete = STATUS_PERCENTS[deliveryStatus];
  const isOrderPlaced = percentComplete >= STATUS_PERCENTS[DeliveryStatus.ORDER_CREATED];
  const isBeingPrepared = percentComplete >= STATUS_PERCENTS[DeliveryStatus.DRIVER_ASSIGNED];
  const isDriverEnroute = percentComplete >= STATUS_PERCENTS[DeliveryStatus.ORDER_PICKED_UP];
  const isDelivered = percentComplete >= STATUS_PERCENTS[DeliveryStatus.ORDER_DROPPED_OFF];
  const statusMessageId = STATUS_MESSAGES[deliveryStatus];
  const ariaLabel = () => {
    const [complete, incomplete] = [t('complete'), t('incomplete')];
    return `
      ${t('orderPlaced')} ${isOrderPlaced ? complete : incomplete},
      ${t('deliveryOrderBeingPrepared')} ${isBeingPrepared ? complete : incomplete},
      ${t('deliveryDriverEnroute')} ${isDriverEnroute ? complete : incomplete},
      ${t('deliveryDelivered')} ${isDelivered ? complete : incomplete},
    `;
  };

  return (
    <Box style={styles.container}>
      <StatusMessageContainer>
        <StatusMessage testID="progress-status-message">
          {!!statusMessageId && t(statusMessageId)}
        </StatusMessage>
      </StatusMessageContainer>
      <VisuallyHidden role="alert" accessibilityLabel={ariaLabel()} />
      <ProgressBar percentComplete={percentComplete} />
      <ProgressList>
        <ProgressListItem>
          <ProgressCheckpoint isActive={isOrderPlaced} />
          {t('orderPlaced')}
        </ProgressListItem>
        <ProgressListItem>
          <ProgressCheckpoint isActive={isBeingPrepared} />
          {t('deliveryOrderBeingPrepared')}
        </ProgressListItem>
        <ProgressListItem>
          <ProgressCheckpoint isActive={isDriverEnroute} />
          {t('deliveryDriverEnroute')}
        </ProgressListItem>
        <ProgressListItem>
          <ProgressCheckpoint isActive={isDelivered} />
          {t('deliveryDelivered')}
        </ProgressListItem>
      </ProgressList>
    </Box>
  );
};

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: spacing.$4,
  },
});

export default DeliveryProgress;
